import React, { useState } from 'react'
import Plot from 'react-plotly.js'

import { makeStyles, Theme } from '@material-ui/core'

import ChartToggleButton from '../ServiceRequestAnalytics/ChartToggleButton'
import {
    ActionItemDetailAnalytics,
    AnalyticsLoadingState,
} from '../../hooks/useEZFolioWorkorderAnalytics'
import { typographyStyle } from '../../theme/typographyStyle'

const useStyles = makeStyles((theme: Theme) => ({
    analyticsDashboard: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: theme.spacing(3),
        padding: theme.spacing(2),
        overflowY: 'auto',
        [theme.breakpoints.up('md')]: {
            gridTemplateColumns: '1fr 1fr',
        },
    },
    chartContainer: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2),
        boxShadow: theme.shadows[1],
        minWidth: 0,
    },
    chartHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        '& h3': {
            margin: 0,
            ...typographyStyle(theme.typography.h6),
        },
    },
}))

interface ActionItemChartsProps {
    loadingState: AnalyticsLoadingState
    analytics: ActionItemDetailAnalytics
}

const ActionItemCharts: React.FC<ActionItemChartsProps> = ({
    analytics,
    loadingState,
}) => {
    const classes = useStyles()
    const [showTimeGraphs, setShowTimeGraphs] = useState({
        services: false,
        locations: false,
    })

    // Transform and sort data for services bar chart
    const servicesData = (() => {
        const sortedServices = Object.entries(analytics.top_services)
            .map(([name, data]) => ({
                name,
                count: data.reduce((sum, { count }) => sum + count, 0),
            }))
            .sort((a, b) => b.count - a.count)

        return {
            x: sortedServices.map((item) => item.count),
            y: sortedServices.map((item) => item.name),
            type: 'bar' as const,
            orientation: 'h' as const,
            marker: {
                color: '#1f77b4',
                opacity: 0.8,
            },
            name: 'Services',
        }
    })()

    // Transform and sort data for locations pie chart
    const locationsData = (() => {
        const sortedLocations = Object.entries(analytics.top_locations)
            .map(([name, data]) => ({
                name,
                count: data.reduce((sum, { count }) => sum + count, 0),
            }))
            .sort((a, b) => b.count - a.count)

        return {
            values: sortedLocations.map((item) => item.count),
            labels: sortedLocations.map((item) => item.name),
            type: 'pie' as const,
            hole: 0.4,
            name: 'Locations',
        }
    })()

    // Transform vendor data for bar chart
    const vendorData = (() => {
        const vendorStats = Object.entries(analytics.vendor_data)
            .map(([name, data]) => ({
                name,
                assigned: data.assigned_count,
                completed: data.completed_count,
            }))
            .sort((a, b) => b.assigned - a.assigned)

        return [
            {
                x: vendorStats.map((item) => item.assigned),
                y: vendorStats.map((item) => item.name),
                name: 'Assigned',
                type: 'bar' as const,
                orientation: 'h' as const,
                marker: { color: '#1f77b4' },
            },
            {
                x: vendorStats.map((item) => item.completed),
                y: vendorStats.map((item) => item.name),
                name: 'Completed',
                type: 'bar' as const,
                orientation: 'h' as const,
                marker: { color: '#2ecc71' },
            },
        ]
    })()

    // Helper function to transform time series data
    const getTimeSeriesData = (data: {
        [key: string]: { date: string; count: number }[]
    }) => {
        return Object.entries(data).map(([name, values]) => ({
            x: values.map((item) => item.date),
            y: values.map((item) => item.count),
            type: 'scatter' as const,
            mode: 'lines+markers' as const,
            name,
        }))
    }

    const toggleTimeGraph = (type: keyof typeof showTimeGraphs) => {
        setShowTimeGraphs((prev) => ({
            ...prev,
            [type]: !prev[type],
        }))
    }

    // Daily trends data
    const dailyTrendsData = [
        {
            x: analytics.assigned_daily.map((item) => item.date),
            y: analytics.assigned_daily.map((item) => item.count),
            type: 'scatter' as const,
            mode: 'lines+markers' as const,
            name: 'Assigned',
            line: { color: '#1f77b4' },
        },
        {
            x: analytics.completed_daily.map((item) => item.date),
            y: analytics.completed_daily.map((item) => item.count),
            type: 'scatter' as const,
            mode: 'lines+markers' as const,
            name: 'Completed',
            line: { color: '#2ecc71' },
        },
    ]

    return (
        <div className={classes.analyticsDashboard}>
            {/* Services Section */}
            <div className={classes.chartContainer}>
                <div className={classes.chartHeader}>
                    <h3>Top Services</h3>
                    <ChartToggleButton
                        showTrend={showTimeGraphs.services}
                        onChange={() => toggleTimeGraph('services')}
                    />
                </div>
                <Plot
                    data={
                        showTimeGraphs.services
                            ? getTimeSeriesData(analytics.top_services)
                            : [servicesData]
                    }
                    layout={{
                        height: 300,
                        margin: {
                            l: 50,
                            r: 40,
                            t: 20,
                            b: showTimeGraphs.services ? 80 : 40,
                        },
                        yaxis: {
                            title: showTimeGraphs.services
                                ? { text: 'Number of Action Items' }
                                : { text: 'Service Type' },
                            automargin: true,
                        },
                        xaxis: showTimeGraphs.services
                            ? {
                                  title: { text: 'Date', standoff: 30 },
                                  type: 'date',
                                  tickangle: -45,
                                  automargin: true,
                                  tickfont: { size: 10 },
                              }
                            : {
                                  title: { text: 'Number of Action Items' },
                              },
                        showlegend: showTimeGraphs.services,
                        legend: showTimeGraphs.services
                            ? {
                                  orientation: 'h',
                                  y: -0.3,
                              }
                            : undefined,
                    }}
                    config={{ responsive: true }}
                    style={{ width: '100%' }}
                />
            </div>

            {/* Locations Section */}
            <div className={classes.chartContainer}>
                <div className={classes.chartHeader}>
                    <h3>Top Action Item Locations</h3>
                    <ChartToggleButton
                        showTrend={showTimeGraphs.locations}
                        onChange={() => toggleTimeGraph('locations')}
                    />
                </div>
                {showTimeGraphs.locations ? (
                    <Plot
                        data={getTimeSeriesData(analytics.top_locations)}
                        layout={{
                            height: 300,
                            margin: { l: 50, r: 40, t: 20, b: 80 },
                            yaxis: {
                                title: { text: 'Number of Action Items' },
                                automargin: true,
                            },
                            xaxis: {
                                title: { text: 'Date', standoff: 30 },
                                type: 'date',
                                tickangle: -45,
                                automargin: true,
                                tickfont: { size: 10 },
                            },
                            showlegend: true,
                            legend: {
                                orientation: 'h',
                                y: -0.3,
                            },
                        }}
                        config={{ responsive: true }}
                        style={{ width: '100%' }}
                    />
                ) : (
                    <Plot
                        data={[locationsData]}
                        layout={{
                            height: 300,
                            margin: { l: 20, r: 20, t: 20, b: 20 },
                            showlegend: true,
                            legend: {
                                orientation: 'h',
                                y: -0.3,
                            },
                        }}
                        config={{ responsive: true }}
                        style={{ width: '100%' }}
                    />
                )}
            </div>

            {/* Daily Trends Section */}
            <div className={classes.chartContainer}>
                <h3>Daily Action Item Trends</h3>
                <Plot
                    data={dailyTrendsData}
                    layout={{
                        height: 300,
                        margin: { l: 50, r: 20, t: 20, b: 80 },
                        xaxis: {
                            title: {
                                text: 'Date',
                                standoff: 30,
                            },
                            tickangle: -45,
                            type: 'date',
                            tickformat: '%Y-%m-%d',
                            automargin: true,
                            tickfont: { size: 10 },
                            ticks: '',
                            showticksuffix: 'all',
                            ticksuffix: '  ',
                        },
                        yaxis: {
                            title: {
                                text: 'Number of Action Items',
                                standoff: 20,
                            },
                            rangemode: 'tozero',
                        },
                        legend: {
                            orientation: 'h',
                            y: -0.3,
                        },
                        hovermode: 'x unified',
                    }}
                    config={{ responsive: true }}
                    style={{ width: '100%' }}
                />
            </div>

            {/* Vendor Performance Section */}
            <div className={classes.chartContainer}>
                <h3>Vendor Performance</h3>
                <Plot
                    data={vendorData}
                    layout={{
                        height: 300,
                        margin: { l: 120, r: 20, t: 20, b: 40 },
                        barmode: 'group',
                        yaxis: {
                            title: { text: 'Vendor' },
                            automargin: true,
                        },
                        xaxis: {
                            title: { text: 'Number of Action Items' },
                        },
                        legend: {
                            orientation: 'h',
                            y: -0.3,
                        },
                        showlegend: true,
                    }}
                    config={{ responsive: true }}
                    style={{ width: '100%' }}
                />
            </div>
        </div>
    )
}

export default ActionItemCharts
